import axios, { type AxiosResponse } from 'axios'

import type { ReportingParametersQueryParams } from '@/api/schemas/reports/common'
import type {
    AddCodeCompliancePatternPayloadSchema,
    AddCodeCompliancePatternResponseSchema,
    BlackBoxAmountPayloadSchema,
    BlackBoxAmountResponseSchema,
    BlackBoxCounterSwitchPayloadSchema,
    BlackBoxCounterSwitchResponseSchema,
    BlackBoxLightPayloadSchema,
    CodeCompliancesResponseSchema,
    CreateCodeCompliancePayloadSchema,
    CreateCodeComplianceResponseSchema,
    CreateReportRequestPayloadSchema,
    CreateReportResponseSchema,
    PrinectAmountPayloadSchema,
    PrinectAmountResponseSchema,
    PrinectMakeReadyAmountResponseSchema,
    ReportDetailResponseSchema,
    UpdateReportPayloadSchema,
    UpdateReportTaskPlatePayloadSchema,
    UpdateReportTaskPlateResponseSchema
} from '@/api/schemas/reports/reportExecution'
import { API_ENDPOINTS } from '@/constants/apiEndpoints'

function buildCodeCompliancesUrl(queryString = '', report = false): string {
    const params = new URLSearchParams()

    if (report) {
        params.set('report', 'true')
    }

    const cleanQueryString = queryString.replace(/^[?&]/, '')

    if (cleanQueryString) {
        const query = new URLSearchParams(cleanQueryString)
        query.forEach((value, key) => {
            params.append(key, value)
        })
    }

    const suffix = params.toString()

    return `${API_ENDPOINTS.REPORTS.RUNTIME.CODE_COMPLIANCES}${suffix ? `?${suffix}` : ''}`
}

class ReportExecutionQueries {
    getReport(
        reportId: number | string,
        params?: ReportingParametersQueryParams
    ): Promise<AxiosResponse<ReportDetailResponseSchema>> {
        return axios.get<ReportDetailResponseSchema>(`${API_ENDPOINTS.REPORTS.EXECUTION.REPORTS}/${reportId}`, { params })
    }

    createReport(
        payload: CreateReportRequestPayloadSchema,
        params?: ReportingParametersQueryParams
    ): Promise<AxiosResponse<CreateReportResponseSchema>> {
        return axios.post<CreateReportResponseSchema>(API_ENDPOINTS.REPORTS.EXECUTION.REPORTS, payload, { params })
    }

    getCodeCompliances(queryString = ''): Promise<AxiosResponse<CodeCompliancesResponseSchema>> {
        return axios.get<CodeCompliancesResponseSchema>(buildCodeCompliancesUrl(queryString))
    }

    generateCodeCompliancesReport(queryString = ''): Promise<AxiosResponse<CodeCompliancesResponseSchema>> {
        return axios.get<CodeCompliancesResponseSchema>(buildCodeCompliancesUrl(queryString, true))
    }

    createCodeCompliance(payload: CreateCodeCompliancePayloadSchema): Promise<AxiosResponse<CreateCodeComplianceResponseSchema>> {
        return axios.post<CreateCodeComplianceResponseSchema>(API_ENDPOINTS.REPORTS.RUNTIME.CODE_COMPLIANCES, payload)
    }

    addCodeCompliancePattern(
        payload: AddCodeCompliancePatternPayloadSchema
    ): Promise<AxiosResponse<AddCodeCompliancePatternResponseSchema>> {
        return axios.post<AddCodeCompliancePatternResponseSchema>(API_ENDPOINTS.REPORTS.RUNTIME.CODE_COMPLIANCE_PATTERNS, payload)
    }

    updateReportTaskPlate(
        reportTaskPlateId: number | string,
        payload: UpdateReportTaskPlatePayloadSchema
    ): Promise<AxiosResponse<UpdateReportTaskPlateResponseSchema>> {
        return axios.patch<UpdateReportTaskPlateResponseSchema>(
            `${API_ENDPOINTS.REPORTS.RUNTIME.REPORT_ITEM_PLATES}/${reportTaskPlateId}`,
            payload
        )
    }

    finishReport(
        reportId: number | string,
        payload: UpdateReportPayloadSchema,
        params?: ReportingParametersQueryParams
    ): Promise<AxiosResponse<ReportDetailResponseSchema>> {
        return axios.put<ReportDetailResponseSchema>(`${API_ENDPOINTS.REPORTS.EXECUTION.REPORTS}/${reportId}`, payload, {
            params
        })
    }

    renewReport(
        reportId: number | string,
        payload: UpdateReportPayloadSchema,
        params?: ReportingParametersQueryParams
    ): Promise<AxiosResponse<ReportDetailResponseSchema>> {
        return axios.put<ReportDetailResponseSchema>(`${API_ENDPOINTS.REPORTS.EXECUTION.REPORTS}/${reportId}`, payload, {
            params
        })
    }

    updateReport(
        reportId: number | string,
        payload: UpdateReportPayloadSchema,
        params?: ReportingParametersQueryParams
    ): Promise<AxiosResponse<ReportDetailResponseSchema>> {
        return axios.put<ReportDetailResponseSchema>(`${API_ENDPOINTS.REPORTS.EXECUTION.REPORTS}/${reportId}`, payload, {
            params
        })
    }

    endMakeReady(
        reportId: number | string,
        payload: UpdateReportPayloadSchema,
        params?: ReportingParametersQueryParams
    ): Promise<AxiosResponse<ReportDetailResponseSchema>> {
        return axios.put<ReportDetailResponseSchema>(`${API_ENDPOINTS.REPORTS.EXECUTION.REPORTS}/${reportId}`, payload, {
            params
        })
    }

    getBlackBoxAmount(payload: BlackBoxAmountPayloadSchema): Promise<AxiosResponse<BlackBoxAmountResponseSchema>> {
        return axios.post<BlackBoxAmountResponseSchema>(API_ENDPOINTS.BLACK_BOXES.COUNTER, payload)
    }

    setBlackBoxLight(payload: BlackBoxLightPayloadSchema): Promise<AxiosResponse<unknown>> {
        return axios.post<unknown>(API_ENDPOINTS.BLACK_BOXES.LIGHTS, payload)
    }

    pauseResumeBlackBoxCounting(
        reportId: number | string,
        payload: BlackBoxCounterSwitchPayloadSchema
    ): Promise<AxiosResponse<BlackBoxCounterSwitchResponseSchema>> {
        return axios.patch<BlackBoxCounterSwitchResponseSchema>(
            `${API_ENDPOINTS.REPORTS.EXECUTION.BLACK_BOX_COUNTER_SWITCH}/${reportId}`,
            payload
        )
    }

    getPrinectAmount(payload: PrinectAmountPayloadSchema): Promise<AxiosResponse<PrinectAmountResponseSchema>> {
        return axios.post<PrinectAmountResponseSchema>(API_ENDPOINTS.REPORTS.EXECUTION.PRINECT_AMOUNT, payload)
    }

    getPrinectMakeReadyAmount(payload: PrinectAmountPayloadSchema): Promise<AxiosResponse<PrinectMakeReadyAmountResponseSchema>> {
        return axios.post<PrinectMakeReadyAmountResponseSchema>(
            API_ENDPOINTS.REPORTS.EXECUTION.PRINECT_MAKE_READY_AMOUNT,
            payload
        )
    }
}

export default new ReportExecutionQueries()
